import { User, Patient, MedicalRecord, Message, Conversation, MedicalRecordItem } from '../types';

// Re-export types to be used by components
export type { User, Patient, MedicalRecord, Message, Conversation, MedicalRecordItem };

// Mock doctor user
export const doctor: User = {
  id: 'doctor-1',
  name: 'Dr. Franziska Weber',
  role: 'doctor',
  avatar: 'https://i.pravatar.cc/150?img=9',
};

// Mock patients
export const patients: Patient[] = [
  {
    id: 'patient-1',
    name: 'Thomas Müller',
    age: 35,
    gender: 'Männlich',
    address: 'Bahnhofstraße 123, 10115 Berlin',
    primaryPhysician: 'Dr. Michael Schmidt',
    insuranceNumber: 'TK-78945612',
    bloodType: 'A+',
    avatar: 'https://i.pravatar.cc/150?img=12',
  },
  {
    id: 'patient-2',
    name: 'Lena Schmidt',
    age: 28,
    gender: 'Weiblich',
    address: 'Hauptstraße 45, 80331 München',
    primaryPhysician: 'Dr. Sabine Weber',
    insuranceNumber: 'AOK-12345678',
    bloodType: 'O-',
    avatar: 'https://i.pravatar.cc/150?img=5',
  },
  {
    id: 'patient-3',
    name: 'Markus Becker',
    age: 42,
    gender: 'Männlich',
    address: 'Königsallee 67, 40212 Düsseldorf',
    primaryPhysician: 'Dr. Thomas Wagner',
    insuranceNumber: 'Barmer-45678912',
    bloodType: 'B+',
    avatar: 'https://i.pravatar.cc/150?img=17',
  },
  {
    id: 'patient-4',
    name: 'Sophie Fischer',
    age: 31,
    gender: 'Weiblich',
    address: 'Schillerstraße 8, 60313 Frankfurt',
    primaryPhysician: 'Dr. Julia Hoffmann',
    insuranceNumber: 'DAK-36985214',
    bloodType: 'AB+',
    avatar: 'https://i.pravatar.cc/150?img=23',
  },
  {
    id: 'patient-5',
    name: 'Klaus Hoffmann',
    age: 55,
    gender: 'Männlich',
    address: 'Domstraße 25, 50667 Köln',
    primaryPhysician: 'Dr. Andreas Bauer',
    insuranceNumber: 'IKK-25814736',
    bloodType: 'O+',
    avatar: 'https://i.pravatar.cc/150?img=53',
  },
];

// Mock medical records
export const medicalRecords: MedicalRecord[] = [
  {
    id: 'record-1',
    patientId: 'patient-1',
    records: [
      {
        id: 'item-1-1',
        date: '2025-05-15',
        type: 'diagnosis',
        title: 'Bluthochdruck',
        description: 'Patient wurde mit Bluthochdruck Stufe 1 diagnostiziert.',
        severity: 'medium',
      },
      {
        id: 'item-1-2',
        date: '2025-05-15',
        type: 'medication',
        title: 'Lisinopril',
        description: 'Verschrieben 10mg täglich zur Blutdruckkontrolle.',
      },
      {
        id: 'item-1-3',
        date: '2025-05-01',
        type: 'session',
        title: 'Erstberatung',
        description: 'Erstes Gespräch über Symptome und Familienanamnese.',
      },
      {
        id: 'item-1-4',
        date: '2025-06-01',
        type: 'treatment',
        title: 'Lebensstiländerungen',
        description: 'Empfohlen: Erhöhte körperliche Aktivität und natriumarme Ernährung.',
      },
      {
        id: 'item-1-5',
        date: '2025-06-20',
        type: 'symptom',
        title: 'Kopfschmerzen',
        description: 'Patient berichtet über wiederkehrende Kopfschmerzen am Morgen.',
        severity: 'medium',
      },
    ],
  },
  {
    id: 'record-2',
    patientId: 'patient-2',
    records: [
      {
        id: 'item-2-1',
        date: '2025-04-10',
        type: 'diagnosis',
        title: 'Saisonale Allergien',
        description: 'Patient zeigt Symptome einer saisonalen Rhinitis.',
        severity: 'low',
      },
      {
        id: 'item-2-2',
        date: '2025-04-10',
        type: 'medication',
        title: 'Cetirizin',
        description: 'Verschrieben 10mg täglich nach Bedarf für Allergiesymptome.',
      },
      {
        id: 'item-2-3',
        date: '2025-06-10',
        type: 'session',
        title: 'Nachsorgetermin',
        description: 'Überprüfung der Wirksamkeit der Allergiemedikation.',
      },
    ],
  },
  {
    id: 'record-3',
    patientId: 'patient-3',
    records: [
      {
        id: 'item-3-1',
        date: '2025-03-20',
        type: 'diagnosis',
        title: 'Diabetes Typ 2',
        description: 'Patient wurde mit Diabetes Typ 2 diagnostiziert.',
        severity: 'medium',
      },
      {
        id: 'item-3-2',
        date: '2025-03-20',
        type: 'medication',
        title: 'Metformin',
        description: 'Verschrieben 500mg zweimal täglich zu den Mahlzeiten.',
      },
      {
        id: 'item-3-3',
        date: '2025-05-20',
        type: 'treatment',
        title: 'Ernährungsberatung',
        description: 'Überweisung an Ernährungsberater für Diabetes-Diät.',
      },
    ],
  },
  {
    id: 'record-4',
    patientId: 'patient-4',
    records: [
      {
        id: 'item-4-1',
        date: '2025-06-05',
        type: 'session',
        title: 'Erstberatung',
        description: 'Besprechung chronischer Rückenschmerzen.',
      },
      {
        id: 'item-4-2',
        date: '2025-06-05',
        type: 'treatment',
        title: 'Physiotherapie',
        description: 'Empfohlen: 8 Wochen Physiotherapie für Rückenschmerzen.',
      },
    ],
  },
  {
    id: 'record-5',
    patientId: 'patient-5',
    records: [
      {
        id: 'item-5-1',
        date: '2025-04-25',
        type: 'diagnosis',
        title: 'Arthrose',
        description: 'Diagnose: Arthrose in beiden Knien.',
        severity: 'medium',
      },
      {
        id: 'item-5-2',
        date: '2025-04-25',
        type: 'medication',
        title: 'Ibuprofen',
        description: 'Verschrieben 400mg nach Bedarf zur Schmerzlinderung.',
      },
      {
        id: 'item-5-3',
        date: '2025-05-25',
        type: 'treatment',
        title: 'Gelenkspritzen',
        description: 'Kortisoninjektionen im rechten Knie verabreicht.',
      },
    ],
  },
];

// Mock conversations
export const conversations: Conversation[] = [
  {
    id: 'conv-1',
    participants: ['doctor-1', 'patient-1'],
    updatedAt: '2025-06-24T15:30:00',
    messages: [
      {
        id: 'msg-1-1',
        senderId: 'patient-1',
        receiverId: 'doctor-1',
        content: 'Hallo Dr. Weber, ich habe in letzter Zeit vermehrt Kopfschmerzen, besonders am Morgen.',
        timestamp: '2025-06-24T15:30:00',
        read: true,
      },
      {
        id: 'msg-1-2',
        senderId: 'doctor-1',
        receiverId: 'patient-1',
        content: 'Guten Tag Herr Müller, das tut mir leid. Wie stark sind diese Kopfschmerzen auf einer Skala von 1-10?',
        timestamp: '2025-06-24T15:35:00',
        read: true,
      },
      {
        id: 'msg-1-3',
        senderId: 'patient-1',
        receiverId: 'doctor-1',
        content: 'Ich würde sagen etwa 6 oder 7. Sie machen es mir schwer, mich bei der Arbeit zu konzentrieren.',
        timestamp: '2025-06-24T15:40:00',
        read: true,
      },
      {
        id: 'msg-1-4',
        senderId: 'doctor-1',
        receiverId: 'patient-1',
        content: 'Haben Sie bemerkt, ob die Kopfschmerzen mit Ihren Blutdruckmedikamenten zusammenhängen? Gibt es andere Symptome?',
        timestamp: '2025-06-24T15:45:00',
        read: false,
      },
    ],
  },
  {
    id: 'conv-2',
    participants: ['doctor-1', 'patient-2'],
    updatedAt: '2025-06-23T10:15:00',
    messages: [
      {
        id: 'msg-2-1',
        senderId: 'patient-2',
        receiverId: 'doctor-1',
        content: 'Hallo Dr. Weber, meine Allergiesymptome scheinen schlimmer zu werden, trotz der Medikamente.',
        timestamp: '2025-06-23T10:15:00',
        read: true,
      },
      {
        id: 'msg-2-2',
        senderId: 'doctor-1',
        receiverId: 'patient-2',
        content: 'Hallo Frau Schmidt, das tut mir leid zu hören. Nehmen Sie die Medikamente täglich wie verschrieben?',
        timestamp: '2025-06-23T10:20:00',
        read: true,
      },
      {
        id: 'msg-2-3',
        senderId: 'patient-2',
        receiverId: 'doctor-1',
        content: 'Ja, jeden Morgen. Aber ich niese immer noch viel und meine Augen jucken.',
        timestamp: '2025-06-23T10:25:00',
        read: true,
      },
    ],
  },
  {
    id: 'conv-3',
    participants: ['doctor-1', 'patient-3'],
    updatedAt: '2025-06-22T14:00:00',
    messages: [
      {
        id: 'msg-3-1',
        senderId: 'doctor-1',
        receiverId: 'patient-3',
        content: 'Hallo Herr Becker, ich melde mich wegen Ihrer aktuellen Laborergebnisse. Ihre HbA1c-Werte haben sich deutlich verbessert. Sehr gut gemacht mit Ihren Ernährungsumstellungen!',
        timestamp: '2025-06-22T14:00:00',
        read: true,
      },
      {
        id: 'msg-3-2',
        senderId: 'patient-3',
        receiverId: 'doctor-1',
        content: 'Das sind tolle Neuigkeiten! Ich bin sehr streng mit meiner Kohlenhydratzufuhr und gehe jeden Tag spazieren.',
        timestamp: '2025-06-22T14:10:00',
        read: true,
      },
    ],
  },
  {
    id: 'conv-4',
    participants: ['doctor-1', 'patient-4'],
    updatedAt: '2025-06-21T09:45:00',
    messages: [
      {
        id: 'msg-4-1',
        senderId: 'patient-4',
        receiverId: 'doctor-1',
        content: 'Hallo Dr. Weber, ich wollte Ihnen mitteilen, dass die Physiotherapie wirklich bei meinen Rückenschmerzen hilft.',
        timestamp: '2025-06-21T09:45:00',
        read: true,
      },
      {
        id: 'msg-4-2',
        senderId: 'doctor-1',
        receiverId: 'patient-4',
        content: 'Das sind ausgezeichnete Nachrichten, Frau Fischer! Machen Sie weiter mit den Übungen, die man Ihnen gegeben hat.',
        timestamp: '2025-06-21T10:00:00',
        read: true,
      },
    ],
  },
  {
    id: 'conv-5',
    participants: ['doctor-1', 'patient-5'],
    updatedAt: '2025-06-20T16:20:00',
    messages: [
      {
        id: 'msg-5-1',
        senderId: 'doctor-1',
        receiverId: 'patient-5',
        content: 'Hallo Herr Hoffmann, wie fühlt sich Ihr Knie nach der Injektion an?',
        timestamp: '2025-06-20T16:20:00',
        read: true,
      },
      {
        id: 'msg-5-2',
        senderId: 'patient-5',
        receiverId: 'doctor-1',
        content: 'Viel besser, danke! Die Schmerzen haben deutlich nachgelassen.',
        timestamp: '2025-06-20T16:30:00',
        read: true,
      },
      {
        id: 'msg-5-3',
        senderId: 'doctor-1',
        receiverId: 'patient-5',
        content: 'Sehr gut! Denken Sie daran, es trotzdem ruhig anzugehen und Überanstrengung zu vermeiden.',
        timestamp: '2025-06-20T16:35:00',
        read: true,
      },
    ],
  },
];

// Utility function to get medical record by patient ID
export const getMedicalRecordByPatientId = (patientId: string): MedicalRecord | undefined => {
  return medicalRecords.find(record => record.patientId === patientId);
};

// Utility function to get conversation by patient ID
export const getConversationByPatientId = (patientId: string): Conversation | undefined => {
  return conversations.find(conv => 
    conv.participants.includes('doctor-1') && conv.participants.includes(patientId)
  );
};

// Utility function to get patient by ID
export const getPatientById = (patientId: string): Patient | undefined => {
  return patients.find(patient => patient.id === patientId);
};
